import {
  Listbox,
  Transition,
  ListboxButton,
  ListboxOptions,
  ListboxOption
} from '@headlessui/react'
import { Fragment, useState, useEffect } from 'react'
import cx from 'classnames'
import { inputClasses, InputSize, CheckboxGroup } from '@/common/components'
import { CaretDownIcon } from '@/common/components/icons'
import { Label } from '@/common/components/InputLabel/InputLabel'
import useI18n from '@/shared/hooks/useI18n'

interface GroupedMultiSelectOption {
  value: string
  labelKey: string
  disabled?: boolean
  groupKey?: number | string
}

interface GroupedMultiSelectProps {
  items: GroupedMultiSelectOption[]
  selectedValues: string[]
  onChange: (values: string[]) => void
  label?: string
  placeholder?: string
  disabled?: boolean
  sizeType?: InputSize
  tooltip?: React.ReactNode
  tooltipPlacement?: 'top' | 'bottom'
  tooltipSize?: 'small' | 'medium' | 'large'
  className?: string
  grouping?: boolean
}

export function GroupedMultiSelect({
  items,
  grouping = false,
  selectedValues,
  onChange,
  label,
  placeholder = '',
  disabled = false,
  sizeType = 'md',
  tooltip,
  tooltipPlacement = 'top',
  tooltipSize = 'small',
  className
}: GroupedMultiSelectProps) {
  const { t } = useI18n()
  const [values, setValues] = useState<string[]>(selectedValues)

  useEffect(() => {
    setValues(selectedValues)
  }, [selectedValues])

  const groups = grouping
    ? Object.values(
        items.reduce<Record<string, { labelKey: string; options: GroupedMultiSelectOption[] }>>(
          (acc, item) => {
            const key = (item.groupKey ?? 'default').toString()
            if (!acc[key]) {
              acc[key] = { labelKey: key, options: [] }
            }
            acc[key].options.push(item)
            return acc
          },
          {}
        )
      )
    : [{ labelKey: '', options: items }]

  const isSelected = (opt: GroupedMultiSelectOption) => values.includes(opt.value)

  const toggleValue = (option: GroupedMultiSelectOption) => {
    let newValues: string[]
    if (isSelected(option)) {
      newValues = values.filter(value => value !== option.value)
    } else {
      newValues = [...values, option.value]
    }
    setValues(newValues)
    onChange(newValues)
  }

  const inputDisplay =
    values.length === 0
      ? ''
      : values
          .map(val => t(items.find(option => option.value === val)?.labelKey || '') || val)
          .join(', ')

  return (
    <div className={cx(className)}>
      {label && (
        <Label
          className="mb-1.5 inline-flex items-center gap-1"
          tooltip={tooltip}
          tooltipPlacement={tooltipPlacement}
          tooltipSize={tooltipSize}
        >
          {label}
        </Label>
      )}

      <Listbox value={values} multiple disabled={disabled}>
        <div className="relative">
          <ListboxButton
            className={cx(
              'text-left border border-border-main pr-7 w-full',
              inputClasses.fixed,
              inputClasses.sizeToClasses[sizeType]
            )}
          >
            <span className={cx({ 'text-text-secondary': values.length === 0 })}>
              {inputDisplay || placeholder}
            </span>
            <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <CaretDownIcon className="size-6" aria-hidden="true" />
            </span>
          </ListboxButton>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="opacity-0 -translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 -translate-y-1"
          >
            <ListboxOptions
              anchor="bottom"
              className="max-h-60 mt-1 overflow-auto shadow-dropdown rounded-xs bg-white border border-border-main focus:outline-none w-[var(--button-width)]"
            >
              {groups.map(group => (
                <div key={group.labelKey} className="not-last-of-type:py-1">
                  {group.labelKey && (
                    <div className="px-3 text-xs py-1 font-semibold text-text-secondary">
                      {t(group.labelKey)}
                    </div>
                  )}
                  {group.options.map(option => (
                    <ListboxOption
                      key={option.value}
                      value={option.value}
                      disabled={option.disabled}
                      className="select-none"
                    >
                      {({ selected: isSelected }) => (
                        <CheckboxGroup
                          id={option.value}
                          checked={isSelected}
                          onChange={() => {
                            if (!option.disabled) {
                              toggleValue(option)
                            }
                          }}
                          className={cx('ml-2 w-full min-h-10 flex items-center cursor-pointer', {
                            'bg-blue-300': isSelected && !option.disabled,
                            'opacity-50 cursor-not-allowed': option.disabled
                          })}
                          label={
                            option.disabled ? (
                              <span className="w-full truncate text-xs font-medium grow pl-2 py-3">
                                {t(option.labelKey)}
                              </span>
                            ) : (
                              t(option.labelKey)
                            )
                          }
                          disabled={option.disabled}
                        />
                      )}
                    </ListboxOption>
                  ))}
                </div>
              ))}
            </ListboxOptions>
          </Transition>
        </div>
      </Listbox>
    </div>
  )
}
